import { useTranslation } from 'react-i18next';
import { useState, useContext } from 'react';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { Button, IconButton, Stepper } from '@teo/components';
import { CrossFilled } from '@teo/components/icons';
import { useMobile } from '@/util';
import { BasicSettings } from './PublishStep/BasicSettings';
import { SharingSettings } from './PublishStep/SharingSettings';
import { Summary } from './PublishStep/Summary';
import { useGet } from '@/query/fetchHooks';
import { Userwithorganisationwithidps } from '@/models';
import { axios } from '@/axios';
import { config } from '@/config';

const PublishLearningPath = ({
  learningpath,
  setIsOpenPublish,
  setIsPiblished,
  learningpathId,
}: any) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;
  const [step, setStep] = useState(1);
  const [globalState, setGlobalState] = useState<any>({
    lpAccess: learningpath?.lpAccess,
  });

  const [checkValid, setCheckValid] = useState<boolean>(false);

  const [lpContent, setLpContent] = useState([]);

  const [allLpGroups, setAllLpGroups] = useState<any>();

  useGet(`/memberlpgroupdetails`, {
    masterId: learningpath?.id,
    organisationId: auth?.user?.activeOrganisationId,
    onSuccess: (res: any) => {
      setAllLpGroups(res?.data || undefined);
    },
  });

  useGet(`/learningpathnodedetails`, {
    learningpathId: learningpath?.id,
    organisationId: authUser?.activeOrganisationId,
    onSuccess: async (res: any) => {
      setLpContent(res?.data || []);
    },
  });

  const handleNextStep = () => {
    setCheckValid(true);
    if (globalState?.name && globalState?.description) {
      setStep(2);
    }
  };

  const handlePublish = async () => {
    const changes: any = {};
    if (globalState?.name !== learningpath?.lpName) {
      changes.lpName = globalState?.name;
    }
    if (globalState?.description !== learningpath?.lpDescription) {
      changes.lpDescription = globalState?.description;
    }

    const promisesBeforePublish: Promise<any>[] = [];

    promisesBeforePublish.push(
      axios.put(`${config.backend}/learningpath/${learningpath?.id}`, {
        id: learningpath?.id,
        lpPublicationStatus: 'published',
        lpAccess: globalState?.lpAccess,
        ...changes,
      })
    );

    await Promise.allSettled(promisesBeforePublish);
    const newPublish = await axios.post(
      `${config.backend}/learningpath/publish/${learningpath?.id}`,
      {
        versionDescription: globalState?.versionComment,
      }
    );

    const promisesAfterPublish: Promise<any>[] = [];

    if (allLpGroups?.length > 0) {
      allLpGroups.map((el: any) => {
        const changes: any = {};

        if (globalState?.name !== learningpath?.lpName) {
          changes.lpgroupName = el?.hasCustomLanding
            ? el.lpgroupName
            : globalState?.name;
        } else {
          changes.lpgroupName = el?.hasCustomLanding
            ? el.lpgroupName
            : learningpath?.lpName;
        }

        if (globalState?.description !== learningpath?.lpDescription) {
          changes.lpgroupDescription = el?.hasCustomLanding
            ? el.lpgroupDescription
            : globalState?.description;
        } else {
          changes.lpgroupDescription = el?.hasCustomLanding
            ? el.lpgroupDescription
            : learningpath?.lpDescription;
        }

        promisesAfterPublish.push(
          axios.put(`${config.backend}/lpgroup/${el?.id}`, {
            // learningpathId: newPublish?.data?.id,
            ...changes,
          })
        );
      });
    }
    const promisesAfterPublishLp: Promise<any>[] = [];

    if (allLpGroups?.length > 0) {
      allLpGroups.map((el: any) => {
        promisesAfterPublishLp.push(
          axios.put(`${config.backend}/learningpath/${el?.learningpathId}`, {
            publishedId: newPublish?.data?.id,
          })
        );
      });
    }

    await Promise.allSettled([promisesAfterPublish]).then(() => {
      document.body.style.overflow = 'unset';
      setIsPiblished(true);
    });
  };

  // console.log(globalState);
  // console.log(learningpath);

  return (
    <div
      style={{ zIndex: 102 }}
      className="fixed top-0 left-0 z-10 flex h-screen w-screen flex-col overflow-y-auto bg-white"
    >
      <div className="flex min-h-20 flex-col justify-start py-2 pr-2 sm:flex-row sm:items-center">
        <div
          data-testid="clouseBtn"
          className="flex min-h-20 items-center justify-start"
        >
          <IconButton
            className="my-auto mx-3 sm:mx-6"
            Icon={CrossFilled}
            variant="ghost"
            onClick={() => {
              setIsOpenPublish(false);
            }}
          />
          <div className="flex items-center">
            <div className="mr-5 h-12 w-20 min-w-20">
              <img
                src={learningpath?.lpPicture || '/assets/images/LearnPath.png'}
                alt="courseFallback"
                className="inline h-12 w-full rounded-lg object-cover sm:h-14"
              />
            </div>
            <div className="flex flex-wrap">
              <span className="pr-2">
                {t(`pages.learning_path.publish.publish`)}
              </span>
              <span className="font-semibold">{learningpath?.lpName}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="h-full border-t border-grey-02">
        <Stepper>
          <Stepper.Item
            isActive={step === 1}
            isDone={step > 1}
            onClick={() => setStep(1)}
            step="1"
          >
            {!isMobile && <>{t('pages.learning_path.publish.step_1')}</>}
          </Stepper.Item>
          <Stepper.Item
            isActive={step === 2}
            isDone={step > 2}
            onClick={() => setStep(2)}
            step="2"
          >
            {!isMobile && <>{t('pages.learning_path.publish.step_2')}</>}
          </Stepper.Item>
          <Stepper.Item
            isActive={step === 3}
            isDone={step > 3}
            onClick={() => setStep(3)}
            step="3"
          >
            {!isMobile && <>{t('pages.learning_path.publish.step_3')}</>}
          </Stepper.Item>
        </Stepper>
        <div className="h-full max-h-[calc(100vh_-_250px)] overflow-y-auto">
          <div className="mx-auto flex max-w-[620px] flex-col justify-center px-3 pt-8">
            {step === 1 && (
              <BasicSettings
                learningpath={learningpath}
                setGlobalState={setGlobalState}
                globalState={globalState}
                checkValid={checkValid}
              />
            )}
            {step === 2 && (
              <SharingSettings
                learningpath={learningpath}
                setGlobalState={setGlobalState}
                globalState={globalState}
                authUser={authUser}
              />
            )}
            {step === 3 && (
              <Summary
                lpContent={lpContent}
                authUser={authUser}
                learningpathId={learningpathId}
              />
            )}
          </div>
        </div>
        <div className="flex border-t border-grey-02 py-5">
          <div className="mx-auto flex w-full max-w-[620px] justify-between px-3">
            <Button variant="ghost" onClick={() => setIsOpenPublish(false)}>
              {t('button.cancel')}
            </Button>
            <div className="flex">
              {step > 1 && (
                <Button
                  variant="outline"
                  className="mr-2"
                  onClick={() => setStep((prev) => prev - 1)}
                >
                  {t('pages.learning_path.publish.previous_step')}
                </Button>
              )}
              {step < 3 && (
                <Button
                  onClick={() =>
                    step === 1 ? handleNextStep() : setStep((prev) => prev + 1)
                  }
                >
                  {t('pages.learning_path.publish.next_step')}
                </Button>
              )}
              {step === 3 && (
                <Button onClick={() => handlePublish()}>
                  {t('pages.learning_path.publish.publish_lp')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishLearningPath;
